import {
  CloseCircleOutlined, FilterOutlined, SearchOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import {
  Button, DatePicker, Divider, Input, Select, Tooltip, Typography,
} from 'antd';
import Checkbox from 'components/Checkbox';
import { pluralize } from 'components/ItemsInput/AddOrderItemOverlay/utils';
import Layout from 'components/Layout';
import LocationSelectInput from 'components/LocationSelectInput';
import UserSelectInput from 'components/UserSelectInput';
import withGuard from 'components/withGuard';
import { PERMISSIONS } from 'constants/permissions';
import RunSequenceDrawer from 'containers/CustomerCenter/Companies/RunSequenceDrawer';
import { useQueryParams } from 'hooks/useQueryParams';
import React, {
  useCallback,
  useMemo, useRef, useState,
} from 'react';
import { captureException } from 'utils/errors';
import MerchantInput from 'components/MerchantInput';
import DateRangeInput from 'components/DateRangeInput';
import dayjs from 'dayjs';
import { serializeFilters } from 'containers/CustomerCenter/Companies/utils';
import ImportCsvDrawer from 'containers/CustomerCenter/Companies/ImportCsvDrawer';
import { formatDate } from 'utils/date';
import DebouncedInput from 'components/DebouncedInput';
import Filter from '../../Companies/Filter';
import { ContactList } from '../utils';
import Table from './Table';

const WORKFLOW_STATUS_OPTIONS = [
  { value: 'Enrolled', label: 'Enrolled' },
  { value: 'Not Enrolled', label: 'Not Enrolled' },
];
// Convert TABS to status options, excluding "All"
const CONTACT_STATUS_OPTIONS =
['Potential', 'Customer', 'Lapsed', 'Churned'].map((label) => ({
  value: label,
  label,
}));

function Customers() {
  const resetFetchRef = useRef(false);
  const [showFilters, setShowFilters] = useState(true);
  const [contacts, setContacts] = useState([]);
  const [sequenceRunnerOpen, setSequenceRunnerOpen] = useState(false);
  const [importCsvOpen, setImportCsvOpen] = useState(false);
  const [filters, setFilters] = useQueryParams({
    search: '',
    status: undefined,
  });

  // Selection state
  const [selectionVersion, setSelectionVersion] = useState(0);
  const selectedCount = ContactList.getSelectedCount();
  const isSelectAllMode = ContactList.getSelection().selectAll;

  // Selection handlers
  const handleSelectAll = (selected) => {
    ContactList.setSelectAll(selected);
    setSelectionVersion((v) => v + 1);
  };

  const handleRowSelect = (record, selected) => {
    ContactList.toggleSelection(record.id);
    setSelectionVersion((v) => v + 1);
  };

  const handleSelectMultiple = (selected, selectedRows, changeRows) => {
    changeRows.forEach((row) => {
      ContactList.toggleSelection(row.id);
    });
    setSelectionVersion((v) => v + 1);
  };

  const clearSelection = () => {
    ContactList.clearSelection();
    setSelectionVersion((v) => v + 1);
  };

  const disabledDate = (current) => current && current.isAfter(dayjs(), 'day');

  function handleFilterChange(payload) {
    resetFetchRef.current = true;
    setFilters((prev) => ({ ...prev, ...payload }));
  }

  const fetchCustomers = useCallback(
    (pagination, filter, sorter) => {
      const page = resetFetchRef.current ? 1 : pagination.current;
      const queries = {
        page,
        page_size: pagination.pageSize,
        ...serializeFilters(filters),
      };

      if (sorter?.order && sorter?.columnKey) {
        queries.ordering = `${sorter?.order === 'descend' ? '-' : ''}${sorter?.columnKey}`;
      }

      resetFetchRef.current = false;

      return ContactList.fetch(queries)
        .then(({ data, total, page }) => {
          setContacts(data);
          return {
            page,
            data,
            total,
          };
        })
        .catch(captureException);
    },
    [
      filters,
    ],
  );

  // Row selection configuration
  const rowSelection = useMemo(() => ({
    type: 'checkbox',
    selectedRowKeys: ContactList.getPageSelectedIds(contacts),
    onSelect: handleRowSelect,
    getCheckboxProps: (record) => ({
      name: record.name,
    }),
    renderCell: (checked, record) => (
      <Checkbox
        checked={checked}
        onClick={(e) => {
          e.stopPropagation();
          handleRowSelect(record);
        }}
      />
    ),
    // Custom logic to handle the "select all" checkbox state across pages
    onSelectAll: (selected, selectedRows, changeRows) => {
      if (selected) {
        handleSelectAll(true);
      } else if (!selected && isSelectAllMode) {
        clearSelection();
      } else {
        // Regular row selection change
        handleSelectMultiple(selected, selectedRows, changeRows);
      }
    },
  }), [contacts, selectionVersion, isSelectAllMode]);

  const hasActiveFiltersCount = Object.entries(filters).filter(([key, value]) => value != null && !['search'].includes(key)).length;

  return (
    <Layout header={(
      <Layout.Header
        title={(
          <div>
            Contacts
            {' '}
            <small style={{ color: 'rgba(0, 0, 0, 0.45)' }}>
              (
              {ContactList.count}
              )
            </small>
          </div>
        )}
        extra={(
          <>
            <Tooltip
              placement="bottomRight"
              title={selectedCount === 0 ? 'please make a selection to run sequences' : ''}
            >
              <Button
                size="large"
                disabled={selectedCount === 0}
                onClick={() => setSequenceRunnerOpen(true)}
              >
                Run Sequences
                {selectedCount > 0 && (
                  <>
                    {' '}
                    (
                    {selectedCount}
                    )
                  </>
                )}
              </Button>
            </Tooltip>

            <Divider type="vertical" className="h-full" />
            <Button
              size="large"
              onClick={() => setImportCsvOpen(true)}
              icon={<UploadOutlined />}
            >
              Import
            </Button>
          </>

        )}
      />
    )}
    >
      <div>
        <div className="flex flex-col gap-3">
          <div className="flex justify-between">
            <DebouncedInput
              placeholder="Search contacts"
              value={filters.search}
              suffix={filters.search ? <CloseCircleOutlined onClick={() => handleFilterChange({ search: '' })} /> : <SearchOutlined style={{ color: 'rgba(0, 0, 0, 0.45)' }} />}
              onChange={(search) => handleFilterChange({ search })}
              style={{ width: 422 }}
              size="large"
            />
            <Button
              color={hasActiveFiltersCount > 0 ? 'primary' : 'default'}
              variant="outlined"
              size="large"
              icon={<FilterOutlined />}
              onClick={() => setShowFilters(!showFilters)}
            >
              {
                hasActiveFiltersCount > 0 ? `${hasActiveFiltersCount} ${pluralize('filter', hasActiveFiltersCount)} selected` : 'Filter'
              }
            </Button>
          </div>

          <div className="gap-2 flex-wrap items-center" style={{ display: showFilters ? 'flex' : 'none' }}>
            <Filter name="Orders from" value={filters.order_from} onChange={(order_from) => handleFilterChange({ order_from })}>
              <MerchantInput
                placeholder="Orders from"
                allowClear
                style={{ width: 250 }}
              />
            </Filter>
            <Divider type="vertical" className="h-4" />
            <Filter
              name="Orders in date range"
              value={filters.order_date}
              onChange={(order_date) => handleFilterChange({ order_date })}
              renderLabel={({ range }) => range && range.map((date) => formatDate(date, { showTime: false })).join(' - ')}
            >
              <DateRangeInput
                placeholder="Orders in date range"
                disabledDate={disabledDate}
              />
            </Filter>
          </div>
          <div className="gap-3 flex-wrap items-start" style={{ display: showFilters ? 'flex' : 'none' }}>
            <Filter name="Status" value={filters.status} onChange={(status) => handleFilterChange({ status })}>
              <Select
                placeholder="Status"
                allowClear
                style={{ width: 150 }}
                options={CONTACT_STATUS_OPTIONS}
              />
            </Filter>

            <Filter
              name="Company owner"
              value={filters.owner}
              onChange={(owner) => handleFilterChange({ owner })}
            >
              <UserSelectInput
                placeholder="Company owner"
                style={{ width: 150 }}
              />
            </Filter>
            <Filter
              name="Market"
              value={filters.market}
              onChange={(market) => handleFilterChange({ market })}
            >
              <LocationSelectInput
                placeholder="Market"
                mode={undefined}
                labelInValue
                style={{ width: 150 }}
              />
            </Filter>
            <Filter
              name="Date created"
              value={filters.created_date}
              onChange={(created_date) => handleFilterChange({ created_date })}
              renderLabel={(value) => value && dayjs(value).format('MMM DD, YYYY')}
            >
              <DatePicker placeholder="Date created" className="w-[150px]" />
            </Filter>
            <Filter
              name="Workflow status"
              value={filters.workflow_status}
              onChange={(workflow_status) => handleFilterChange({ workflow_status })}
            >
              <Select
                placeholder="Workflow status"
                allowClear
                style={{ width: 150 }}
                options={WORKFLOW_STATUS_OPTIONS}
              />
            </Filter>
          </div>
        </div>
        <div
          className="flex items-center mt-2"
          style={{ visibility: selectedCount > 0 ? 'visible' : 'hidden' }}
        >
          <Typography.Text className="m-2">
            {
              isSelectAllMode ? (
                <>
                  <strong>
                    All
                    {' '}
                    {ContactList.count}
                    {' '}
                    contacts
                  </strong>
                  {' '}
                  are selected
                  {ContactList.getSelection().excludedIds.length > 0 && (
                    <>
                      {' '}
                      (except
                      {' '}
                      {ContactList.getSelection().excludedIds.length}
                      {' '}
                      deselected)
                    </>
                  )}
                </>
              ) : (
                <>
                  <strong>{selectedCount}</strong>
                  {' '}
                  {pluralize('contact', selectedCount)}
                  {' '}
                  selected
                </>
              )
            }
          </Typography.Text>
          <Button
            size="small"
            type="link"
            onClick={clearSelection}
          >
            Clear selection
          </Button>
        </div>

        <Table
          fetchCustomers={fetchCustomers}
          rowSelection={rowSelection}
        />
        <RunSequenceDrawer
          open={sequenceRunnerOpen}
          onClose={() => setSequenceRunnerOpen(false)}
          contactFilters={serializeFilters(filters)}
          selections={ContactList.getSelection()}
          onFinish={clearSelection}
        />
        <ImportCsvDrawer
          open={importCsvOpen}
          onClose={() => setImportCsvOpen(false)}
        />
      </div>
    </Layout>
  );
}

export default withGuard(Customers, [PERMISSIONS.CUSTOMERS], { redirect: true });
